import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';

import {
  iconBrush,
  iconMinus,
  iconPlus,
  iconProductImage,
} from '@/components/icons';
import { baseStyles, typographyStyles } from '@/styles';
import './fg-personalisation-file';

@customElement('fg-personalisation-choose-visual-designer')
@localized()
export class FgPersonalisationChooseVisualDesigner extends LitElement {
  render() {
    const uspsVisualDesigner = {
      pro: [
        'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp1',
        'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp2',
        'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp3',
        'content.personalisation.visualDesignerStep.options.visualDesigner.usps.usp4',
      ],
    };
    const uspsManual = {
      pro: [
        'content.personalisation.visualDesignerStep.options.manual.usps.usp1',
      ],
      con: [
        'content.personalisation.visualDesignerStep.options.manual.usps.usp2',
        'content.personalisation.visualDesignerStep.options.manual.usps.usp3',
        'content.personalisation.visualDesignerStep.options.manual.usps.usp4',
      ],
    };

    return html`
      <div class="cards">
        <div class="wrapper">
          <button
            class="card"
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent('select-method', {
                  detail: {
                    visualDesigner: true,
                  },
                }),
              );
              this.dispatchEvent(
                new CustomEvent('handle-announcement', {
                  detail: {
                    announcement: t(
                      `content.personalisation.visualDesignerStep.options.visualDesigner.ariaSelectedTitle`,
                    ),
                  },
                }),
              );
            }}
          >
            <span class="label" aria-hidden="true">${t('labels.new')}</span>
            <span class="icon" aria-hidden="true">${iconBrush}</span>
            <div class="content">
              <header>
                <h2>
                  ${t(
                    'content.personalisation.visualDesignerStep.options.visualDesigner.title',
                  )}
                </h2>
              </header>
              <p>
                ${t(
                  'content.personalisation.visualDesignerStep.options.visualDesigner.description',
                )}
              </p>
              <ul>
                ${Object.entries(uspsVisualDesigner).map(([key, usps]) =>
                  Object.values(usps).map(
                    usp => html`
                      <li>
                        <span class="procon-icon ${key}" aria-hidden="true">
                          ${key === 'pro' ? iconPlus : iconMinus}
                        </span>
                        ${t(usp)}
                      </li>
                    `,
                  ),
                )}
              </ul>
            </div>
          </button>
          <footer>${t('labels.quickAndEasy')}</footer>
        </div>
        <div class="wrapper">
          <button
            @click=${() => {
              this.dispatchEvent(
                new CustomEvent('select-method', {
                  detail: {
                    url: false,
                  },
                }),
              );
              this.dispatchEvent(
                new CustomEvent('handle-announcement', {
                  detail: {
                    announcement: t(
                      `content.personalisation.visualDesignerStep.options.manual.ariaSelectedTitle`,
                    ),
                  },
                }),
              );
            }}
          >
            <span class="icon" aria-hidden="true">${iconProductImage}</span>
            <div class="content">
              <header>
                <h2>
                  ${t(
                    'content.personalisation.visualDesignerStep.options.manual.title',
                  )}
                </h2>
              </header>
              <p>
                ${t(
                  'content.personalisation.visualDesignerStep.options.manual.description',
                )}
              </p>
              <ul>
                ${Object.entries(uspsManual).map(([key, usps]) => {
                  return html`
                    ${Object.values(usps).map(usp => {
                      return html`
                        <li>
                          <span class="procon-icon ${key}" aria-hidden="true">
                            ${key === 'pro' ? iconPlus : iconMinus}
                          </span>
                          ${t(usp)}
                        </li>
                      `;
                    })}
                  `;
                })}
              </ul>
            </div>
          </button>
        </div>
      </div>
    `;
  }
  static styles = [
    baseStyles,
    typographyStyles,
    css`
      .cards {
        display: grid;
        gap: 4px;
        grid-template-columns: 1fr 1fr;
      }
      .wrapper {
        position: relative;
        z-index: 1;
      }
      button {
        background: white;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        display: flex;
        flex-direction: column;
        margin-bottom: 44px;
        padding-top: 45px;
        position: relative;
        width: 100%;
      }
      button.card:not(:focus) {
        outline: 3px solid var(--fg-secondary, var(--fg-fallback-secondary));
        outline-offset: -3px;
      }
      button:hover,
      button:focus {
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        cursor: pointer;
      }
      .label {
        background-color: var(--fg-cta, var(--fg-fallback-cta));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        color: white;
        display: block;
        font-weight: bold;
        left: 8px;
        padding: 4px 12px;
        position: absolute;
        text-transform: capitalize;
        top: 8px;
        width: fit-content;
      }
      .icon {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        display: grid;
        height: 64px;
        place-content: center;
        text-align: center;
        width: 100%;
      }
      .content {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        justify-content: end;
        padding: 0 16px 8px;
      }
      h2 {
        margin: 24px 0 0 0;
      }
      p {
        margin: 0;
        padding: 4px 0 0 0;
      }
      ul {
        list-style-type: none;
        margin-left: -4px;
        margin-top: 0;
        padding: 0;
      }
      li {
        display: flex;
        margin: 6px 0;
      }
      li span {
        flex-shrink: 0;
        margin-right: 3px;
      }
      .procon-icon {
        display: grid;
        height: 16px;
        margin-top: 3px;
        place-content: center;
        width: 16px;
      }
      .procon-icon.pro {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
      }
      .procon-icon.con {
        color: var(--fg-red--500, var(--fg-fallback-red--500));
      }
      footer {
        background: var(--fg-secondary, var(--fg-fallback-secondary));
        color: white;
        display: grid;
        border-bottom-left-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        border-bottom-right-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        bottom: 6px;
        font-size: 1.125rem;
        height: 44px;
        padding-top: 3px;
        place-content: center;
        position: absolute;
        text-align: center;
        width: 100%;
        z-index: -1;
      }
    `,
  ];
}
